import { channelCustomTypes, userMetaDataKeys } from '@inbox-demo-nx/constants';
import { useAuthContext } from '@inbox-demo-nx/contexts';
import { View } from 'dripsy';
import { ComponentProps, useMemo } from 'react';
import SendBird from 'sendbird';

import Avatar from './Avatar';
import BrandAvatar from './BrandAvatar';
import ImageAvatar from './ImageAvatar';

export default function ChannelCover({
  channel,
  size = 50,
  style,
}: {
  channel: SendBird.GroupChannel;
  size?: number;
  style?: ComponentProps<typeof BrandAvatar>['style'];
}) {
  const { currentUser } = useAuthContext();
  const otherChannelMember = useMemo(
    () =>
      channel.members.length === 2
        ? channel.members.find(
            (member) => member.userId !== currentUser?.userId
          )
        : undefined,
    [channel.members, currentUser?.userId]
  );

  function renderAvatar() {
    if (channel.customType === channelCustomTypes.support) {
      return <BrandAvatar size={size} style={style} type="support" />;
    }
    if (channel.customType === channelCustomTypes.notifications) {
      return <BrandAvatar size={size} style={style} type="notifications" />;
    }
    if (channel.customType === channelCustomTypes.promotions) {
      return <BrandAvatar size={size} style={style} type="promotions" />;
    }
    if (channel.customType === channelCustomTypes.delivery) {
      return <BrandAvatar size={size} style={style} type="delivery" />;
    }

    if (channel.members.length === 1) {
      return <Avatar size={size} style={style} user={channel.members[0]} />;
    }
    if (channel.members.length === 2) {
      return <Avatar size={size} style={style} user={otherChannelMember} />;
    }
    if (channel.members.length > 2) {
      return (
        <View
          style={{
            width: size,
            height: size,
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            transform: [{ scale: 0.9 }],
          }}
        >
          {channel.members.slice(0, 4).map((user, index) => (
            <Avatar
              key={index}
              user={user}
              style={{ transform: [{ scale: 1.3 }] }}
              size={size / 2}
            />
          ))}
        </View>
      );
    }
    if (channel.coverUrl) {
      return <ImageAvatar size={size} source={{ uri: channel.coverUrl }} />;
    }
    return <View style={{ width: size, height: size }} />;
  }

  function renderBadge() {
    const badgeSize = size * 0.32;

    if (
      badgeSize < 8 ||
      otherChannelMember == null ||
      otherChannelMember.metaData[userMetaDataKeys.userType] === 'official' ||
      channel.customType === channelCustomTypes.delivery
    ) {
      return null;
    }

    if (otherChannelMember?.connectionStatus === 'online') {
      return (
        <View
          sx={{
            backgroundColor: 'online',
            position: 'absolute',
            right: 0,
            bottom: 0,
            borderColor: 'white',
            borderWidth: 2,
            width: badgeSize,
            height: badgeSize,
            borderRadius: badgeSize / 2,
          }}
        />
      );
    }
    return null;
  }

  return (
    <View
      style={[
        {
          width: size,
          height: size,
          borderRadius: size / 2,
        },
        style,
      ]}
    >
      {renderAvatar()}
      {renderBadge()}
    </View>
  );
}
